import { useEffect, useRef, useState } from 'react';
import { PulseGrid } from '../components/PulseGrid';
import { PulseHeader } from '../components/PulseHeader';
import { PulseFooter } from '../components/PulseFooter';
import { PulseRecentRow } from '../components/PulseRecentRow';
import { usePulseStore } from '../hooks/usePulseStore';
import { usePulseStream } from '../hooks/usePulseStream';
import { usePulseRanking } from '../hooks/usePulseRanking';
import { useGridConfig } from '../hooks/useGridConfig';
import { BURNIN_DRIFT_INTERVAL_MS, BURNIN_DRIFT_PX } from '../constants';
import '../pulse.css';

/**
 * Platform Pulse — full-screen, dark-only, TV-optimized real-time video matrix.
 * Renders outside AppShell (no sidebar). Requires admin auth via route guard.
 */
export function PulsePage() {
  const config = useGridConfig();
  const { connected } = usePulseStream();
  usePulseRanking(config);
  const stats = usePulseStore((s) => s.stats);
  const containerRef = useRef<HTMLDivElement>(null);
  const [showReconnecting, setShowReconnecting] = useState(false);

  // Only show reconnecting overlay after 5s of disconnection (avoids flash on initial load).
  useEffect(() => {
    if (connected) {
      setShowReconnecting(false);
      return;
    }
    const timer = setTimeout(() => setShowReconnecting(true), 5000);
    return () => clearTimeout(timer);
  }, [connected]);

  // Anti-burn-in: subtle drift to prevent OLED burn-in on static elements.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    let dx = 0;
    let dy = 0;
    let dirX = 1;
    let dirY = 1;
    const interval = setInterval(() => {
      dx += BURNIN_DRIFT_PX * dirX;
      dy += BURNIN_DRIFT_PX * dirY;
      if (Math.abs(dx) >= 3) dirX *= -1;
      if (Math.abs(dy) >= 3) dirY *= -1;
      el.style.transform = `translate(${dx}px, ${dy}px)`;
    }, BURNIN_DRIFT_INTERVAL_MS);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="pulse-page fixed inset-0 overflow-hidden bg-[#0a0a0f]">
      <div ref={containerRef} className="flex h-full w-full flex-col">
        <PulseHeader stats={stats} connected={connected} />
        <div className="relative min-h-0 flex-1">
          <PulseGrid config={config} />
        </div>
        <PulseRecentRow />
        <PulseFooter stats={stats} />
      </div>

      {/* Reconnecting overlay — only shown after 5s of disconnection */}
      {showReconnecting && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/60">
          <div className="flex flex-col items-center gap-3">
            <div className="size-8 animate-spin rounded-full border-2 border-[#0194f5] border-t-transparent" />
            <span className="text-sm text-[#e0e0e0]/70">Reconnecting…</span>
          </div>
        </div>
      )}
    </div>
  );
}
